<template>
  <div class="bg-card border border-border rounded-lg flex flex-col gap-3 p-4">
    <div class="w-full relative">
      <Search class="absolute left-3 top-1/2 -translate-y-1/2 text-newPrimary h-4 w-4" />
      <input
        v-model="searchQuery"
        placeholder="Search Talent Communities, companies or skills..."
        class="w-full h-9 pl-9 pr-4 text-sm bg-white border border-border rounded-md
        focus:ring-0 focus:border-border"
      />
    </div>

    <div class="w-full flex flex-col lg:flex-row lg:items-center gap-3">
      <div class="flex items-center">
        <Funnel class="h-5 w-5 text-newPrimary" />
      </div>

      <div class="w-full flex flex-col sm:flex-row lg:flex-nowrap gap-3">
        <div class="flex-1 min-w-[10rem]">
        <Listbox v-model="selectedWorkType">
          <div class="relative">
            <ListboxButton class="relative w-full h-9 cursor-default bg-white  border border-border rounded-md py-2 pl-3 pr-10 text-left text-sm ">

              <span class="block truncate">{{ selectedWorkType?.name ?? 'All Types' }}</span>
              <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown class="h-4 w-4 text-newPrimary" />
              </span>
            </ListboxButton>
            <Transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
              <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                <ListboxOption
                  v-for="workType in workTypesForFilter"
                  :key="workType.id"
                  :value="workType"
                  v-slot="{ active, selected }"
                >
                  <li :class="[active ? 'bg-gray-100' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-10 pr-4']">
                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">{{ workType.name }}</span>
                    <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                      <Check class="h-4 w-4 text-newPrimary" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </Transition>
          </div>
        </Listbox>
      </div>

        <div class="flex-1 min-w-[10rem]">
        <Listbox v-model="selectedIndustry">
          <div class="relative">
            <ListboxButton class="relative w-full h-9 cursor-default bg-white  border border-border rounded-md py-2 pl-3 pr-10 text-left text-sm ">
              <span class="block truncate">{{ selectedIndustry?.name ?? 'All Industries' }}</span>
              <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown class="h-4 w-4 text-newPrimary" />
              </span>
            </ListboxButton>
            <Transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
              <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                <ListboxOption
                  v-for="industry in industryOptions"
                  :key="industry.id ?? 'all-industries'"
                  :value="industry"
                  v-slot="{ active, selected }"
                >
                  <li :class="[active ? 'bg-gray-100' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-10 pr-4']">
                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">{{ industry.name }}</span>
                    <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                      <Check class="h-4 w-4 text-newPrimary" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </Transition>
          </div>
        </Listbox>
      </div>

        <div class="flex-1 min-w-[10rem]">
        <Listbox v-model="selectedOpportunity">
          <div class="relative">
              <ListboxButton class="relative w-full h-9 cursor-default bg-white  border border-border rounded-md py-2 pl-3 pr-10 text-left text-sm ">
              <span class="block truncate">{{ selectedOpportunity.name }}</span>
              <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown class="h-4 w-4 text-newPrimary" />
              </span>
            </ListboxButton>
            <Transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
              <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                <ListboxOption
                  v-for="opportunity in opportunities"
                  :key="opportunity.id"
                  :value="opportunity"
                  v-slot="{ active, selected }"
                >
                  <li :class="[active ? 'bg-gray-100' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-10 pr-4']">
                    <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">{{ opportunity.name }}</span>
                    <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                      <Check class="h-4 w-4 text-newPrimary" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </Transition>
          </div>
        </Listbox>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// ===================================
// 1. IMPORTS
// ===================================
import { ref, watch, onMounted, computed } from 'vue';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { Search, ChevronDown, Funnel, Check } from 'lucide-vue-next';
import { storeToRefs } from 'pinia'
import { useIndustryStore } from '@/stores/industryStore'

// ===================================
// 2. PROPS & EMITS
// ===================================
// Define the event this component will send to its parent
const emit = defineEmits(['filters-changed']);

// ===================================
// 3. STATE
// ===================================
const searchQuery = ref('');
const selectedOpportunity = ref({ id: null, name: 'All Talent Communities' });
const selectedWorkType = ref({ id: null, name: 'All Types' });
const selectedIndustry = ref({ id: null, name: 'All Industries' });

const workTypesForFilter = ref([
  { id: null, name: 'All Types' },
  { id: 'contract', name: 'Contract' },
  { id: 'permanent', name: 'Permanent' },
  { id: 'either', name: 'Either' }
]);

const opportunities = ref([
  { id: null, name: 'All Talent Communities' },
  { id: 'available', name: 'Available to Apply' },
  { id: 'my-applications', name: 'My Applications' },
]);

// ===================================
// 4. COMPUTED PROPERTIES
// ===================================
const industryStore = useIndustryStore()
const { industries } = storeToRefs(industryStore)

const industryOptions = computed(() => {
  const base = [{ id: null, name: 'All Industries' }]
  if (!industries.value?.length) return base
  return base.concat(industries.value.map((industry) => ({
    id: industry.id,
    name: industry.name
  })))
})

// ===================================
// 5. WATCHERS
// ===================================
watch([searchQuery, selectedWorkType, selectedOpportunity, selectedIndustry], () => {
  emit('filters-changed', {
    query: searchQuery.value,
    status: selectedOpportunity.value?.id,
    // This was sending the name, change it to send the id
    type: selectedWorkType.value?.id,
    industry: selectedIndustry.value?.id
  });
});

// ===================================
// 6. LIFECYCLE HOOKS
// ===================================
onMounted(() => {
  industryStore.fetchIndustries()
});

// onUnmounted(() => {
//   // ...
// });


// ===================================
// 7. FUNCTIONS
// ===================================

</script>
